/**
 * Create-CV flow: the pure, platform-agnostic decision layer.
 *
 * The old screen asked one flat question ("Generate with AI / Upload resume /
 * Start blank") that silently fused two unrelated decisions: WHERE the content
 * comes from, and WHAT it gets tailored to. Users could not tell which CV
 * "Generate with AI" would build on (the answer was: never one of theirs, it
 * always read the master profile).
 *
 * The flow asks those two questions in order, as two steps of ONE wizard (they
 * are not two tabs: step 2 always operates on what step 1 produced, so the user
 * can never be looking at a tailoring choice that has no starting point):
 *
 *   Step 1 - START FROM      Step 2 - TAILOR
 *   Existing CV          ->  Optional CV skill (filtered for the chosen source)
 *   Upload a resume      ->  Optional job description
 *   Blank                    (step 2 is skipped entirely)
 *
 * Which tailoring is legal is NOT a hand-maintained table: it falls out of the
 * skill's own `inputKind` (skill-run-spec.ts). A skill that reads a stored CV
 * (`cv` / `cv_jd`) can only tailor a source that has a CV. Legacy profile
 * deep links are normalized to the Main CV before the flow is displayed.
 *
 * This module holds no React and no I/O so both the native and web screens can
 * be thin twins over one tested decision set.
 */
import type { SkillSummary } from '@tabbio/types';
import { runSpecFor, type SkillRunSpec } from '../../skills/skill-run-spec';
import { skillType } from '../../skills/skill-taxonomy';
import { MASTER_CV_ID } from '../../../lib/cv-main-default';

// ── Vocabulary ───────────────────────────────────────────────────────────────

/** Step 1: where this CV's content comes from. */
export type CreateCvSource = 'existing' | 'upload' | 'profile' | 'blank';

/** Step 2: what it gets tailored to. `none` keeps the content as-is. */
export type CreateCvTailorMode = 'none' | 'skill' | 'jd';

export type CreateCvStep = 'source' | 'tailor';

export interface CreateCvState {
  step: CreateCvStep;
  source: CreateCvSource | null;
  /** Required when source is `existing`; set by the flow when source is `upload`. */
  sourceCvId: string | null;
  /**
   * The CV an uploaded resume already became, once the import has run.
   *
   * Submitting is not atomic: the upload becomes a CV first, then the tailoring
   * runs on it. If the tailoring fails the user is still looking at the flow and
   * will press the button again - and without this, plan resolution would import
   * the same resume a SECOND time and leave a stray CV behind on every press.
   * Recording the id makes the retry resume from the tailoring beat.
   */
  importedCvId: string | null;
  title: string;
  tailorMode: CreateCvTailorMode;
  /** Selected CV skill (step 2, `skill` mode). */
  skillId: string | null;
  /** Pasted job description (step 2, `jd` mode, and `cv_jd` skills). */
  jobDescription: string;
}

export const INITIAL_CREATE_CV_STATE: CreateCvState = {
  step: 'source',
  source: null,
  sourceCvId: null,
  importedCvId: null,
  title: '',
  tailorMode: 'none',
  skillId: null,
  jobDescription: '',
};

/** Minimum pasted-JD length before the tailoring is worth sending to the model. */
export const MIN_JOB_DESCRIPTION_LENGTH = 10;

// ── Step 1 → step 2 availability ─────────────────────────────────────────────

/**
 * A source "carries a CV" when step 2 has a concrete CV document to tailor:
 * an existing CV, or the one an uploaded resume becomes. `profile` tailors the
 * master profile instead, and `blank` has nothing to tailor at all.
 */
export function sourceCarriesCv(source: CreateCvSource | null): boolean {
  return source === 'existing' || source === 'upload';
}

/** Whether step 2 is offered at all for this source. */
export function sourceSupportsTailoring(source: CreateCvSource | null): boolean {
  return source !== null && source !== 'blank';
}

/** The tailoring modes offered in step 2 for a given step-1 source. */
export function tailorModesForSource(
  source: CreateCvSource | null,
): readonly CreateCvTailorMode[] {
  return sourceSupportsTailoring(source)
    ? (['skill', 'jd'] as const)
    : (['none'] as const);
}

// ── Wizard shape ─────────────────────────────────────────────────────────────

/**
 * How many steps this flow has for the chosen source. A blank CV has nothing to
 * tailor, so the wizard collapses to a single step and submits straight away.
 *
 * "Nothing picked yet" counts as the full two steps on purpose: the progress
 * bar is drawn before the user answers anything, and a bar that shows one
 * segment and then grows a second one the instant a source is tapped reads as a
 * glitch rather than as progress.
 */
export function createCvStepCount(source: CreateCvSource | null): 1 | 2 {
  return source === 'blank' ? 1 : 2;
}

/** 1-based position of the current step, for "Step {n} of {total}". */
export function createCvStepIndex(state: CreateCvState): 1 | 2 {
  return state.step === 'tailor' && sourceSupportsTailoring(state.source) ? 2 : 1;
}

/**
 * The step the flow should actually be on. Guards deep links and source changes
 * that would otherwise strand the user on a tailor step for a source that has
 * nothing to tailor.
 */
export function resolveCreateCvStep(state: CreateCvState): CreateCvStep {
  return sourceSupportsTailoring(state.source) ? state.step : 'source';
}

// ── CV skill selection ───────────────────────────────────────────────────────

/**
 * Whether a skill's run spec can tailor the given source. CV-writing skills
 * split cleanly by what they READ:
 *   - `cv` / `cv_jd` need a concrete CV from the existing or upload source.
 *   - `profile` is kept only for compatibility with legacy skill deep links.
 * Anything else (text/topic/job skills) never belongs in this picker; those
 * live in the Skills hub and produce markdown, not a CV.
 */
export function runSpecFitsSource(
  spec: SkillRunSpec,
  source: CreateCvSource | null,
): boolean {
  if (!sourceSupportsTailoring(source)) return false;
  if (spec.outputKind !== 'cv_edit') return false;
  if (spec.inputKind === 'cv' || spec.inputKind === 'cv_jd') {
    return sourceCarriesCv(source) || source === 'profile';
  }
  if (spec.inputKind === 'profile') {
    return source === 'existing' || source === 'profile';
  }
  return false;
}

/**
 * Run spec for a LISTED skill. The list DTO carries no manifest `run` block
 * (that only arrives with the detail fetch), so this resolves from the seed map
 * + category fallback, exactly like the chat slash menu does.
 */
export function specForSkill(skill: SkillSummary): SkillRunSpec {
  return runSpecFor(skill.skillId, skill.categories ?? []);
}

/**
 * The CV skills offered in step 2 for a source. Filters by BOTH the cv/other
 * discriminator (so "other" skills never leak into a CV-building flow, even if
 * the server filter is unavailable) and the source fit above.
 */
export function cvSkillsForSource(
  skills: readonly SkillSummary[],
  source: CreateCvSource | null,
): SkillSummary[] {
  if (!sourceSupportsTailoring(source)) return [];
  return skills.filter(
    (skill) =>
      skillType(skill) === 'cv' && runSpecFitsSource(specForSkill(skill), source),
  );
}

/** A `cv_jd` skill wants the job description box shown alongside it. */
export function skillWantsJobDescription(
  skill: SkillSummary | null | undefined,
): boolean {
  if (!skill) return false;
  return specForSkill(skill).inputKind === 'cv_jd';
}

/**
 * Same question, answerable from a bare id. A skill deep link (`?skillId=…`)
 * selects a skill the catalog has not necessarily loaded yet, and without this
 * the flow would let a `cv_jd` skill through with no job description.
 */
export function stateWantsJobDescription(
  state: Pick<CreateCvState, 'tailorMode' | 'skillId'>,
  skill?: SkillSummary | null,
): boolean {
  if (state.tailorMode !== 'skill') return false;
  if (skill) return skillWantsJobDescription(skill);
  if (!state.skillId) return false;
  return runSpecFor(state.skillId).inputKind === 'cv_jd';
}

// ── Validation ───────────────────────────────────────────────────────────────

export interface CreateCvValidation {
  /** Step 1 is answered well enough to move to step 2. */
  canContinue: boolean;
  /** The whole flow is answered well enough to create the CV. */
  canSubmit: boolean;
  /** i18n key under `cv.create.errors.*`, or null when nothing is blocking. */
  errorKey: string | null;
}

export function validateCreateCv(
  state: CreateCvState,
  context: {
    selectedSkill?: SkillSummary | null;
    /**
     * False while a picked skill still has a required run-screen field the user
     * has not answered. The field list lives on the skill DETAIL, which only the
     * hook fetches, so completeness arrives here as a plain boolean.
     */
    skillInputsComplete?: boolean;
    /**
     * True when the picked skill declares its OWN run-screen fields. It then
     * asks for the job post itself (every v2 CV skill declares `job_post`), so
     * the flow hides its separate JD editor - and this gate has to agree, or
     * the button blocks on a field that is not on screen.
     */
    skillCollectsOwnInputs?: boolean;
  } = {},
): CreateCvValidation {
  const blocked = (errorKey: string): CreateCvValidation => ({
    canContinue: false,
    canSubmit: false,
    errorKey,
  });

  if (!state.source) return blocked('sourceRequired');
  if (state.source === 'existing' && !state.sourceCvId) {
    return blocked('sourceCvRequired');
  }
  // The name is optional for EVERY source, which is what the field's own hint
  // promises. A blank CV used to be the one exception, but that gate was
  // invisible on a form sheet — the button simply stopped working with no
  // toast to explain it — and the caller already defaults a title per source.

  const canContinue = true;

  if (state.tailorMode === 'skill') {
    if (!state.skillId) {
      return { canContinue, canSubmit: false, errorKey: 'skillRequired' };
    }
    // The server refuses a run that is missing a declared required input
    // (SKILL_INPUT_REQUIRED). Ask here instead of failing there.
    if (context.skillInputsComplete === false) {
      return { canContinue, canSubmit: false, errorKey: 'skillInputsRequired' };
    }
    if (
      !context.skillCollectsOwnInputs &&
      stateWantsJobDescription(state, context.selectedSkill) &&
      state.jobDescription.trim().length < MIN_JOB_DESCRIPTION_LENGTH
    ) {
      return { canContinue, canSubmit: false, errorKey: 'jobDescriptionRequired' };
    }
  }

  if (
    state.tailorMode === 'jd' &&
    state.jobDescription.trim().length < MIN_JOB_DESCRIPTION_LENGTH
  ) {
    return { canContinue, canSubmit: false, errorKey: 'jobDescriptionRequired' };
  }

  return { canContinue, canSubmit: true, errorKey: null };
}

// ── The primary button ───────────────────────────────────────────────────────

/** What pressing the primary button does from here. */
export type CreateCvIntent = 'continue' | 'create' | 'create_tailored';

export interface CreateCvPrimaryAction {
  intent: CreateCvIntent;
  /** i18n key under `cv.create.actions.*`. */
  labelKey: string;
  /** True while the button is greyed out. */
  disabled: boolean;
  /** True when pressing runs an AI plan (sparkle icon + "writing…" copy). */
  isAi: boolean;
}

export interface CreateCvPrimaryContext {
  step: CreateCvStep;
  /** False while the `upload` source is still waiting on a parsed resume. */
  resumeReady: boolean;
  /** `validateCreateCv(...).canSubmit`. */
  canSubmit: boolean;
  /** A write is already in flight. */
  busy: boolean;
}

/**
 * Step 1 greys out only on the STRUCTURAL answers — no source, no CV picked, no
 * resume read. A missing CV name stays pressable on purpose, so the press can
 * say which field is missing instead of leaving a dead button with no
 * explanation. Step 2 waits for full validity only when the user chose a
 * tailoring action. With no action selected, `none` is a valid raw-copy path.
 */
export function resolveCreateCvPrimaryAction(
  state: CreateCvState,
  context: CreateCvPrimaryContext,
): CreateCvPrimaryAction {
  const onSourceStep = resolveCreateCvStep({ ...state, step: context.step }) === 'source';
  // Matched to the progress bar, so the label and the segments agree even
  // before a source is picked.
  const isContinue = onSourceStep && createCvStepCount(state.source) === 2;
  const isAi = !isContinue && state.tailorMode !== 'none';

  const structurallyBlocked =
    !state.source ||
    (state.source === 'existing' && !state.sourceCvId) ||
    (state.source === 'upload' && !context.resumeReady);

  return {
    intent: isContinue ? 'continue' : isAi ? 'create_tailored' : 'create',
    labelKey: isContinue ? 'continue' : isAi ? 'createTailored' : 'create',
    disabled:
      context.busy ||
      (onSourceStep ? structurallyBlocked : !context.canSubmit),
    isAi,
  };
}

// ── Pressing Continue on step 1 ──────────────────────────────────────────────

/** What pressing the primary button on step 1 should do about the upload. */
export type CreateCvSourceStepAction =
  /** Read the resume sitting unsent in the composer, then stay put. */
  | 'submit_resume'
  /** Nothing to read and nothing read yet: say so. */
  | 'blocked'
  /** The upload is settled (or irrelevant); carry on to the tailor step. */
  | 'proceed';

/**
 * The composer has its own send arrow, and the wizard has Continue. Those used
 * to be different buttons with different powers: a CV attached but not sent
 * left Continue greyed out, so the only way forward was a second button the
 * user had no reason to look for. Continue now means "use what is in the box".
 */
export function resolveCreateCvSourceStepAction(context: {
  /** The source is `upload` and no resume has been parsed yet. */
  uploadIncomplete: boolean;
  /** The composer holds unsent text or an attached file. */
  draftPending: boolean;
  /** A parse is already running. */
  resumeBusy: boolean;
}): CreateCvSourceStepAction {
  if (!context.uploadIncomplete) return 'proceed';
  if (context.resumeBusy) return 'blocked';
  return context.draftPending ? 'submit_resume' : 'blocked';
}

// ── Plan resolution ──────────────────────────────────────────────────────────

/**
 * What the submit button actually does. Every branch maps onto an API surface
 * that already exists:
 *   import    → POST /api/cv/from-parsed          (parsed resume, one call)
 *   reuse     → nothing to write: the imported CV already IS the result
 *   duplicate → POST /api/cv/:id/duplicate  ("a new version of this CV")
 *   blank     → POST /api/cv with an emptied overrides snapshot
 *   profile   → POST /api/cv (resolver falls back to the master profile)
 *   generate  → POST /api/cv/generate  (now accepts sourceCvId)
 *   skill_run → POST /api/skills/:id/run  (`writeMode` decides sibling vs update)
 *
 * `upload` resolves in two beats: the resume is imported first, then the plan is
 * re-resolved against the CV it produced (see `planAfterImport`).
 */
/** How a skill run writes its result. Mirrors the API's `mode`. */
export type SkillWriteMode = 'update' | 'new_version';

export type CreateCvPlan =
  | { kind: 'import' }
  /**
   * The upload already became a CV on an earlier press and no tailoring is
   * wanted: that CV is the finished result. Without this the flow fell through
   * to `duplicate` and made a copy of its own import.
   */
  | { kind: 'reuse'; cvId: string }
  | { kind: 'blank' }
  | { kind: 'profile' }
  | { kind: 'duplicate'; sourceCvId: string }
  | { kind: 'generate'; sourceCvId: string | null; prompt: string }
  | {
      kind: 'skill_run';
      skillId: string;
      /**
       * null when the source is the master profile: the submit hook then
       * materializes the profile CV first and the skill updates it in place.
       */
      cvId: string | null;
      /**
       * Where the run writes. `new_version` protects a CV the USER picked by
       * writing a sibling; `update` is for a CV this flow just made for the
       * purpose (an imported resume, a materialized profile copy), where a
       * sibling would only leave the intermediate behind as a duplicate.
       */
      writeMode: SkillWriteMode;
      jobDescription: string | null;
    };

export function resolveCreateCvPlan(state: CreateCvState): CreateCvPlan {
  // The resume has to become a CV before anything can tailor it - unless it
  // already did on an earlier press, in which case that CV is the source.
  if (state.source === 'upload' && !state.importedCvId) return { kind: 'import' };

  // An imported resume outranks `sourceCvId`: it is the CV this flow is about.
  const cvId =
    state.source === 'profile'
      ? null
      : (state.importedCvId ?? state.sourceCvId);

  if (state.tailorMode === 'skill' && state.skillId) {
    // `new_version` is only for a CV the USER picked and expects to keep. A CV
    // this flow made for the run - the imported resume, or the profile copy the
    // submit hook materializes - IS the deliverable, so the run updates it in
    // place rather than leaving the intermediate behind as a second CV.
    const flowOwnsCv =
      Boolean(state.importedCvId) || cvId === null || cvId === MASTER_CV_ID;
    return {
      kind: 'skill_run',
      skillId: state.skillId,
      cvId,
      writeMode: flowOwnsCv ? 'update' : 'new_version',
      jobDescription: state.jobDescription.trim() || null,
    };
  }

  if (state.tailorMode === 'jd') {
    return {
      kind: 'generate',
      sourceCvId: cvId,
      prompt: state.jobDescription.trim(),
    };
  }

  // No optional tailoring action means "create it as-is". A CV this flow
  // imported is already exactly that; a CV the user picked has to be copied so
  // the original is left alone. Profile starts as a raw profile CV.
  if (state.importedCvId) return { kind: 'reuse', cvId: state.importedCvId };
  if (sourceCarriesCv(state.source) && cvId) {
    return { kind: 'duplicate', sourceCvId: cvId };
  }
  if (state.source === 'blank') return { kind: 'blank' };
  return { kind: 'profile' };
}

/**
 * Re-resolve after an uploaded resume has been persisted as `cvId`. Returns the
 * tailoring plan for that new CV, or null when no tailoring was requested (the
 * imported CV is already the finished result: nothing more to run).
 */
export function planAfterImport(
  state: CreateCvState,
  cvId: string,
): CreateCvPlan | null {
  if (state.tailorMode === 'none') return null;
  return resolveCreateCvPlan({ ...state, importedCvId: cvId });
}

// ── Deep links ───────────────────────────────────────────────────────────────

/**
 * Maps the legacy `?source=` values (jobs, the resume-import notice, chat) onto
 * the new vocabulary so existing entry points keep landing somewhere sensible:
 * `ai` used to mean "generate from a pasted JD", which is now profile + JD.
 */
export function readSourceParam(value?: string | null): Partial<CreateCvState> {
  switch (value) {
    case 'upload':
      return { source: 'upload', step: 'source' };
    case 'blank':
      return { source: 'blank', step: 'source' };
    case 'ai':
      return {
        source: 'existing',
        sourceCvId: MASTER_CV_ID,
        tailorMode: 'jd',
        step: 'tailor',
      };
    case 'existing':
      return { source: 'existing', step: 'source' };
    default:
      return {};
  }
}

/** Prefill from a deep link that already knows the skill to apply. */
export function readSkillParam(
  skillId?: string | null,
  skill?: SkillSummary | null,
): Partial<CreateCvState> {
  if (!skillId) return {};
  const spec = skill ? specForSkill(skill) : runSpecFor(skillId);
  return {
    skillId,
    tailorMode: 'skill',
    source: 'existing',
    sourceCvId: spec.inputKind === 'profile' ? MASTER_CV_ID : null,
    step: spec.inputKind === 'profile' ? 'tailor' : 'source',
  };
}
